import React, { useState, useCallback, useEffect } from 'react'
import logo from '../images/logo.png'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LogoutIcon from '@mui/icons-material/Logout'
import { Typography, Box, Chip } from '@mui/material'

import { CustomRoomTable } from './CustomRoomTable'
import MainMenu from './MainMenu'
import { useAppSelector, useAppDispatch } from '../hooks'
import { setAuthenticated, setUserData } from '../stores/UserStore'
import back4appService from '../services/back4app'
import { setupRoutingListeners, initializeRouting } from '../stores/routingStore'

import { getPhaserGameSync } from '../PhaserGame'
import type Bootstrap from '../scenes/Bootstrap'
import { setLoadingGame } from '../stores/GameStore'
import { PermissionsDialog } from './PermissionsDialog'

// Import styled components
import {
  Backdrop,
  Wrapper,
  CustomRoomWrapper,
  TitleWrapper,
  Title,
  Content,
  ProgressBarWrapper,
  ProgressBar,
  LogoutButton,
  UserInfoBox,
} from './styles/RoomSelectionDialog.styles'

interface DialogState {
  showCustomRoom: boolean
  showSnackbar: boolean
  showLegacyUI: boolean
  showPermissionsDialog: boolean
}

const initialDialogState: DialogState = {
  showCustomRoom: false,
  showSnackbar: false,
  showLegacyUI: false,
  showPermissionsDialog: false,
}

/**
 * Dialog component for room selection and management
 * Handles room creation, joining, and user authentication
 */
export default function RoomSelectionDialog(): JSX.Element {
  const [dialogState, setDialogState] = useState<DialogState>(initialDialogState)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)

  const lobbyJoined = useAppSelector((state) => state.room.lobbyJoined)
  const userData = useAppSelector((state) => state.user.userData)
  const userRole = useAppSelector((state) => state.user.userRole)
  const dispatch = useAppDispatch()
  const settings = useAppSelector((state) => state.workspace.settings)
  const platformName = settings?.platform_name || 'Sevv'

  const updateDialogState = useCallback((updates: Partial<DialogState>) => {
    setDialogState((prev) => ({ ...prev, ...updates }))
  }, [])

  // Initialize routing system
  useEffect(() => {
    setupRoutingListeners()
    initializeRouting()
  }, [])

  const handleConnect = useCallback(() => {
    if (lobbyJoined) {
      updateDialogState({ showPermissionsDialog: true })
    } else {
      updateDialogState({ showSnackbar: true })
    }
  }, [lobbyJoined, updateDialogState])

  const handlePermissionsGranted = useCallback(() => {
    const phaserGame = getPhaserGameSync()
    if (!phaserGame) {
      setErrorMessage('Game not initialized. Please wait and try again.')
      return
    }
    
    const bootstrap = phaserGame.scene?.keys?.bootstrap as Bootstrap | undefined
    if (bootstrap?.network) {
      bootstrap.network
        .joinOrCreatePublic(true)
        .then(() => {
          // Get the room state to extract mapId if available
          const roomState = bootstrap.network.getRoomState()
          const mapId = roomState?.mapId
          console.log(`Launching Public Lobby with map ID: ${mapId}`)

          // Launch game with the correct map ID
          bootstrap.launchGame({
            mapId: mapId,
            fromRoomCreation: false,
          })
        })
        .catch((error) => {
          console.error('Error joining room:', error)
          setErrorMessage('Failed to join room. Please try again.')
        })
    }
  }, [])

  const handleLogout = useCallback(async () => {
    setLogoutConfirmOpen(false)
    try {
      await back4appService.authService.signOut()
      dispatch(setUserData(null))
      dispatch(setAuthenticated(false))
    } catch (error) {
      console.error('Error logging out:', error)
      setErrorMessage('Failed to logout. Please try again.')
    }
  }, [dispatch])

  const getRoleColor = useCallback((role: string): string => {
    switch (role) {
      case 'admin':
        return '#f44336' // red
      case 'moderator':
        return '#2196f3' // blue
      case 'user':
      default:
        return '#4caf50' // green
    }
  }, [])

  if (!dialogState.showLegacyUI) {
    return (
      <>
        <MainMenu onJoinPublicLobby={handleConnect} useRouting={true} />

        <PermissionsDialog
          open={dialogState.showPermissionsDialog}
          onClose={() => updateDialogState({ showPermissionsDialog: false })}
          onPermissionsGranted={handlePermissionsGranted}
          network={(() => {
            if (!lobbyJoined) return undefined
            const phaserGame = getPhaserGameSync()
            const bootstrap = phaserGame?.scene?.keys?.bootstrap as Bootstrap | undefined
            return bootstrap?.network
          })()}
        />

        <Snackbar
          open={dialogState.showSnackbar}
          autoHideDuration={3000}
          onClose={() => updateDialogState({ showSnackbar: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error">Please wait for connection to establish.</Alert>
        </Snackbar>

        {errorMessage && (
          <Snackbar
            open={!!errorMessage}
            autoHideDuration={5000}
            onClose={() => setErrorMessage('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="error" onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          </Snackbar>
        )}
      </>
    )
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={dialogState.showSnackbar}
        autoHideDuration={3000}
        onClose={() => updateDialogState({ showSnackbar: false })}
      >
        <Alert
          severity="error"
          variant="outlined"
          style={{ background: '#fdeded', color: '#7d4747' }}
        >
          Trying to connect to server, please try again!
        </Alert>
      </Snackbar>

      {errorMessage && (
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={5000}
          onClose={() => setErrorMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        </Snackbar>
      )}

      <Backdrop>
        <Wrapper>
          <LogoutButton color="secondary" onClick={() => setLogoutConfirmOpen(true)} aria-label="Log out">
            <Tooltip title="Log out">
              <LogoutIcon />
            </Tooltip>
          </LogoutButton>

          <Dialog
            open={logoutConfirmOpen}
            onClose={() => setLogoutConfirmOpen(false)}
            aria-labelledby="room-logout-confirm-title"
          >
            <DialogTitle id="room-logout-confirm-title">Log out of Sevv?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                You&apos;ll need to sign in again to get back to your workspace.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLogoutConfirmOpen(false)} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button onClick={handleLogout} variant="contained" sx={{ textTransform: 'none' }}>
                Log out
              </Button>
            </DialogActions>
          </Dialog>
          {dialogState.showCustomRoom ? (
            <CustomRoomWrapper>
              <TitleWrapper>
                <IconButton
                  className="back-button"
                  onClick={() => updateDialogState({ showCustomRoom: false })}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Title>Custom Rooms</Title>
              </TitleWrapper>
              <CustomRoomTable onBack={() => updateDialogState({ showCustomRoom: false })} />
            </CustomRoomWrapper>
          ) : (
            <Content>
              <img src={logo} alt={platformName} />
              <Typography variant="h4" style={{ color: '#eee' }}>
                {platformName}
              </Typography>
              <Typography variant="body1" style={{ color: '#aaa' }}>
                Select a room to join
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConnect}
                  disabled={!lobbyJoined}
                >
                  Join Public Room
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => updateDialogState({ showCustomRoom: true })}
                >
                  Custom Rooms
                </Button>
              </Box>
            </Content>
          )}
        </Wrapper>
      </Backdrop>
    </>
  )
}
